import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, RotateCcw, Warehouse, Receipt, Tablet, BookOpen, Calculator, Send, Search } from "lucide-react";
import { OpenapiStudioPanel } from "@/components/SdiPanel";
import { AvatarButton, CredenzialiButton } from "@/components/CompanyAccess";
import { RegimeDialog, REGIMI_COGE } from "@/components/coge/RegimeDialog";
import { Switch } from "@/components/ui/switch";

const MODULI = { magazzino: "Magazzino fiscale", corrispettivi: "Corrispettivi telematici", primanota: "Prima nota cassa e banca" };
import { api, errMsg, REGIMI } from "@/lib/api";
import { PageHeader, Field, Empty, QuotaBar } from "@/components/Common";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { PrintButton } from "@/components/PrintButton";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "@/components/DateField";

const EMPTY = { denominazione: "", piva: "", codice_fiscale: "", regime_fiscale: "RF01", indirizzo: "", cap: "", comune: "", provincia: "",
  nazione: "IT", email: "", pec: "", ateco: "", studio_id: null, package_id: null, active: true, admin_email: "", admin_password: "", admin_name: "" };
const FIELDS = [["denominazione", "Ragione sociale", "sm:col-span-2"], ["piva", "Partita IVA"], ["codice_fiscale", "Codice fiscale"],
  ["indirizzo", "Indirizzo", "sm:col-span-2"], ["cap", "CAP"], ["comune", "Comune"], ["provincia", "Provincia"], ["email", "Email"], ["pec", "PEC"], ["ateco", "Codice ATECO"]];
const NONE = "__none";

const Pick = ({ label, value, onChange, items, testid, labelKey = "denominazione" }) => (
  <Field label={label}>
    <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? null : v)}>
      <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>Nessuno</SelectItem>
        {items.map((s) => <SelectItem key={s.id} value={s.id}>{s[labelKey]}{s.xml_quota ? ` · ${s.xml_quota} XML` : ""}</SelectItem>)}
      </SelectContent>
    </Select>
  </Field>
);

export default function Companies() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [studios, setStudios] = useState([]);
  const [pkgs, setPkgs] = useState([]);
  const [edit, setEdit] = useState(null);
  const [mag, setMag] = useState(null);
  const [reg, setReg] = useState(null);
  const [ambiente, setAmbiente] = useState(null);
  const load = useCallback(() => api.get("/admin/companies").then((r) => setItems(r.data)), []);
  useEffect(() => {
    load();
    api.get("/admin/studios").then((r) => setStudios(r.data));
    api.get("/admin/packages").then((r) => setPkgs(r.data));
  }, [load]);

  const save = async () => {
    try {
      const body = { ...edit, admin_email: edit.admin_email || null, admin_password: edit.admin_password || null };
      if (edit.id) await api.put(`/admin/companies/${edit.id}`, body);
      else await api.post("/admin/companies", body);
      toast.success("Azienda salvata");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const recharge = async (id) => {
    try { await api.post(`/admin/companies/${id}/recharge`); toast.success("Pacchetto ricaricato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const saveMag = async () => {
    try {
      const { data } = await api.put(`/admin/companies/${mag.id}/${mag.key}`, { attivo: mag.attivo, prezzo: Number(mag.prezzo || 0), scadenza: mag.scadenza || null });
      toast.success(`${MODULI[mag.key]} ${data.attivo ? "attivo" : mag.attivo ? "salvato ma non attivo (scadenza passata)" : "disattivato"}`);
      setMag(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const magOn = (c, key = "magazzino") => c[key]?.attivo && (!c[key].scadenza || c[key].scadenza >= new Date().toISOString().slice(0, 10));
  const openMod = (c, key) => setMag({ key, id: c.id, nome: c.denominazione, attivo: !!c[key]?.attivo, prezzo: c[key]?.prezzo || 0, scadenza: c[key]?.scadenza || "" });
  const toggleAgenti = async (c) => {
    try {
      await api.put(`/admin/companies/${c.id}/agenti`, { attivo: !c.agenti_attivo });
      toast.success(c.agenti_attivo ? "Agenti di vendita disattivati: gli agenti non possono più accedere" : "Agenti di vendita attivati: l'azienda può creare gli account agente");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleCorrImport = async (c) => {
    try {
      await api.put(`/admin/companies/${c.id}/corr-import`, { attivo: !c.corr_import });
      toast.success(c.corr_import ? "Importazione corrispettivi disattivata" : "Importazione corrispettivi attivata");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const trialAct = async (c, act, msg) => {
    if (act === "attiva-piano" && !window.confirm(`Attivare il piano per ${c.denominazione}? La prova termina e l'invio SdI viene sbloccato.`)) return;
    try { await api.post(`/admin/companies/${c.id}/${act}`); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleOpenapi = async (c) => {
    const on = !(c.openapi?.configured && (!ambiente || c.openapi_ambiente === ambiente));
    if (on && !window.confirm(`Registrare ${c.denominazione} su Openapi (firma e conservazione a pagamento sul tuo account)?`)) return;
    try {
      await api.put(`/admin/companies/${c.id}/openapi`, { attivo: on });
      toast.success(on ? "Azienda registrata su Openapi: invierà con Openapi" : "Openapi disattivato per questa azienda");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Aziende clienti" subtitle="Assegna pacchetto XML e studio di riferimento a ogni azienda">
        <PrintButton testid="print-companies-button" path="/stampe/admin/aziende.pdf" />
        <Button data-testid="add-company-button" className="bg-sky-600 hover:bg-sky-700" onClick={() => setEdit({ ...EMPTY })}><Plus className="mr-2 h-4 w-4" />Nuova azienda</Button>
      </PageHeader>
      <OpenapiStudioPanel className="mb-6" onAmbiente={(a) => { setAmbiente(a); load(); }} />
      <div className="relative mb-4 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input data-testid="companies-search-input" className="bg-white pl-9" placeholder="Cerca per nome, P.IVA, studio o pacchetto…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {items && items.length === 0 ? <Empty text="Nessuna azienda" /> : (
        <div data-testid="companies-table" className="divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white">
              {(items || []).filter((c) => !q.trim() || [c.denominazione, c.piva, c.codice_fiscale, c.studio_name, c.package_name].some((v) => (v || "").toLowerCase().includes(q.trim().toLowerCase()))).map((c) => (
                <div key={c.id} data-testid={`company-row-${c.id}`} className="p-4 transition-colors hover:bg-slate-50/60">
                  <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.6fr)_8rem_minmax(0,1fr)_7rem_12rem] lg:items-center">
                  <div className="font-semibold text-slate-900">{c.denominazione}
                    {c.trial && <span className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-semibold ${c.trial_fine < new Date().toISOString().slice(0, 10) ? "bg-rose-100 text-rose-700" : "bg-sky-100 text-sky-800"}`} data-testid={`company-trial-${c.id}`}>Prova fino al {c.trial_fine.split("-").reverse().join("/")}</span>}
                    {c.trial && <span className="ml-1 whitespace-nowrap">
                      <button type="button" className="text-xs text-emerald-700 underline" data-testid={`company-activate-${c.id}`} onClick={() => trialAct(c, "attiva-piano", "Piano attivato")}>Attiva piano</button>
                      <button type="button" className="ml-2 text-xs text-sky-700 underline" data-testid={`company-extend-${c.id}`} onClick={() => trialAct(c, "proroga-prova", "Prova prorogata di 15 giorni")}>+15 giorni</button>
                    </span>}
                  </div>
                  <div className="font-mono text-xs text-slate-600"><span className="mr-1 font-sans text-[10px] uppercase text-slate-400 lg:hidden">P.IVA</span>{c.piva}</div>
                  <div className="text-sm"><span className="mr-1 text-[10px] uppercase text-slate-400 lg:hidden">Studio</span>{c.studio_name || <span className="text-slate-400">—</span>}</div>
                  <div className="text-sm"><span className="mr-1 text-[10px] uppercase text-slate-400 lg:hidden">Pacchetto</span>{c.package_name || <span className="text-slate-400">—</span>}</div>
                  <QuotaBar used={c.xml_used} quota={c.xml_quota} />
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-x-1 gap-y-1 border-t border-dashed border-slate-200 pt-2">
                    <Button variant="ghost" size="sm" title="Invio SdI con firma e conservazione tramite Openapi" data-testid={`openapi-toggle-${c.id}`}
                      className={c.openapi?.configured ? ((!ambiente || c.openapi_ambiente === ambiente) ? "text-emerald-700" : "whitespace-nowrap text-amber-700") : "text-slate-600"} onClick={() => toggleOpenapi(c)}>
                      <Send className="mr-1 h-4 w-4" />{c.openapi?.configured ? ((!ambiente || c.openapi_ambiente === ambiente) ? "Openapi" : "Riattiva Openapi") : "No Openapi"}
                    </Button>
                    <Button variant="ghost" size="sm" title="Pacchetto aggiuntivo magazzino fiscale" data-testid={`warehouse-toggle-${c.id}`}
                      className={magOn(c) ? "text-emerald-700" : "text-slate-600"} onClick={() => openMod(c, "magazzino")}>
                      <Warehouse className="mr-1 h-4 w-4" />{magOn(c) ? "Magazzino" : "No magazzino"}
                    </Button>
                    <Button variant="ghost" size="sm" title="Pacchetto aggiuntivo corrispettivi telematici" data-testid={`receipts-toggle-${c.id}`}
                      className={magOn(c, "corrispettivi") ? "text-emerald-700" : "text-slate-600"} onClick={() => openMod(c, "corrispettivi")}>
                      <Receipt className="mr-1 h-4 w-4" />{magOn(c, "corrispettivi") ? "Corrispettivi" : "No corrispettivi"}
                    </Button>
                    <Button variant="ghost" size="sm" title="Abbonamento prima nota cassa e banca" data-testid={`primanota-toggle-${c.id}`}
                      className={magOn(c, "primanota") ? "text-emerald-700" : "text-slate-600"} onClick={() => openMod(c, "primanota")}>
                      <BookOpen className="mr-1 h-4 w-4" />{magOn(c, "primanota") ? "Prima nota" : "No prima nota"}
                    </Button>
                    <Button variant="ghost" size="sm" title="Tipo di contabilità" data-testid={`regime-btn-${c.id}`} className="text-slate-700" onClick={() => setReg(c)}>
                      <Calculator className="mr-1 h-4 w-4" />Contab. {REGIMI_COGE[c.coge_regime || "ordinaria"].toLowerCase()}
                    </Button>
                    <Button variant="ghost" size="sm" title="Importazione corrispettivi trasmessi" data-testid={`corr-import-toggle-${c.id}`}
                      className={c.corr_import ? "text-emerald-700" : "text-slate-600"} onClick={() => toggleCorrImport(c)}>
                      <Receipt className="mr-1 h-4 w-4" />{c.corr_import ? "Import corr." : "No import corr."}
                    </Button>
                    <Button variant="ghost" size="sm" title="Agenti di vendita da tablet" data-testid={`agents-toggle-${c.id}`}
                      className={c.agenti_attivo ? "text-emerald-700" : "text-slate-600"} onClick={() => toggleAgenti(c)}>
                      <Tablet className="mr-1 h-4 w-4" />{c.agenti_attivo ? "Agenti" : "No agenti"}
                    </Button>
                    <span className="ml-auto" />
                    <Button variant="ghost" size="icon" title="Ricarica pacchetto" data-testid={`recharge-company-${c.id}`} onClick={() => recharge(c.id)}><RotateCcw className="h-4 w-4" /></Button>
                    <AvatarButton c={c} />
                    <CredenzialiButton c={c} />
                    <Button variant="ghost" size="icon" data-testid={`edit-company-${c.id}`} onClick={() => setEdit({ ...EMPTY, ...c, admin_email: "", admin_password: "" })}><Pencil className="h-4 w-4" /></Button>
                    <ConfirmDelete testid={`delete-company-${c.id}`} text="Verranno eliminati anche documenti, anagrafiche e utenti dell'azienda." onConfirm={async () => { await api.delete(`/admin/companies/${c.id}`); load(); }} />
                  </div>
                </div>
              ))}
        </div>
      )}
      {reg && <RegimeDialog company={reg} onClose={() => setReg(null)} onSaved={() => { setReg(null); load(); }} />}
      <Dialog open={!!mag} onOpenChange={(o) => !o && setMag(null)}>
        <DialogContent data-testid="warehouse-dialog">
          <DialogHeader><DialogTitle>{MODULI[mag?.key] || ""} · {mag?.nome}</DialogTitle></DialogHeader>
          {mag && (
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="flex items-center justify-between rounded-lg border border-slate-200 p-3 text-sm sm:col-span-2">
                <span>Pacchetto aggiuntivo attivo</span>
                <Switch data-testid="warehouse-active-switch" checked={mag.attivo} onCheckedChange={(v) => setMag({ ...mag, attivo: v })} />
              </label>
              <Field label="Prezzo concordato € (facoltativo)"><Input data-testid="warehouse-price-input" type="number" step="0.01" value={mag.prezzo} onChange={(e) => setMag({ ...mag, prezzo: e.target.value })} /></Field>
              <Field label="Scadenza (vuoto = senza scadenza)"><DateField clearable testid="warehouse-expiry-input" value={mag.scadenza} onChange={(v) => setMag({ ...mag, scadenza: v })} /></Field>
            </div>
          )}
          <DialogFooter><Button data-testid="save-warehouse-button" onClick={saveMag}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>{edit?.id ? "Modifica azienda" : "Nuova azienda"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid gap-4 sm:grid-cols-2">
              {FIELDS.map(([k, l, c]) => (
                <Field key={k} label={l} className={c}><Input data-testid={`company-${k}-input`} value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} /></Field>
              ))}
              <Field label="Regime fiscale">
                <Select value={edit.regime_fiscale} onValueChange={(v) => setEdit({ ...edit, regime_fiscale: v })}>
                  <SelectTrigger data-testid="company-regime-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(REGIMI).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Pick label="Studio abbinato" testid="company-studio-select" value={edit.studio_id} items={studios} onChange={(v) => setEdit({ ...edit, studio_id: v })} />
              <Pick label="Pacchetto XML" testid="company-package-select" value={edit.package_id} items={pkgs} labelKey="name" onChange={(v) => setEdit({ ...edit, package_id: v })} />
              {!edit.id && (
                <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:col-span-2">
                  <div className="text-sm font-semibold">Primo amministratore azienda (facoltativo)</div>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <Field label="Nome"><Input data-testid="company-admin-name-input" value={edit.admin_name} onChange={(e) => setEdit({ ...edit, admin_name: e.target.value })} /></Field>
                    <Field label="Email"><Input data-testid="company-admin-email-input" type="email" value={edit.admin_email} onChange={(e) => setEdit({ ...edit, admin_email: e.target.value })} /></Field>
                    <Field label="Password"><Input data-testid="company-admin-password-input" value={edit.admin_password} onChange={(e) => setEdit({ ...edit, admin_password: e.target.value })} /></Field>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button data-testid="save-company-button" disabled={!edit?.denominazione || !edit?.piva} onClick={save}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
